import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button.jsx'
import { Input } from '@/components/ui/input.jsx'
import { Label } from '@/components/ui/label.jsx'
import { Textarea } from '@/components/ui/textarea.jsx'
import { Check, ShieldCheck } from 'lucide-react'
import { formStart, formSubmit } from '@/utils/analytics'
import { formatDate } from '@/utils/dateUtils'

/**
 * Contact form component with Cloudflare Turnstile CAPTCHA protection.
 * Submits data to Formspree.
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {function} props.onOpenChange - Callback to change dialog open state.
 */
function ContactForm({ open, onOpenChange }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    message: ''
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [turnstileLoaded, setTurnstileLoaded] = useState(false)
  const [turnstileError, setTurnstileError] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const captchaRef = useRef(null)
  const analyticsStartRef = useRef(Date.now())
  const captchaRenderedRef = useRef(false)
  const widgetIdRef = useRef(null)
  const abortControllerRef = useRef(null)

  const formspreeId = import.meta.env.VITE_FORMSPREE_ID_CONTACT || 'mgvzdoyq'
  const endpoint = `https://formspree.io/f/${formspreeId}`

  // Load Cloudflare Turnstile script and set loaded flag
  useEffect(() => {
    const existing = document.getElementById('cf-turnstile-script')
    if (!existing) {
      const script = document.createElement('script')
      script.id = 'cf-turnstile-script'
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.defer = true
      script.onload = () => setTurnstileLoaded(true)
      document.head.appendChild(script)
    } else if (window.turnstile) {
      setTurnstileLoaded(true)
    }
  }, [])

  // Fire form_start when dialog opens
  useEffect(() => {
    if (open) {
      analyticsStartRef.current = Date.now()
      formStart('contact')
    }
  }, [open])

  // Cleanup abort controller on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Cleanup Turnstile widget on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          // Widget may already be removed or invalid - safe to ignore
        }
        widgetIdRef.current = null
        captchaRenderedRef.current = false
      }
    }
  }, [])

  // Memoized callback for Turnstile widget to prevent recreating on every render
  const handleCaptchaSuccess = useCallback((token) => {
    setCaptchaToken(token)
    setErrors(prev => ({ ...prev, captcha: '' }))
  }, [])

  const handleCaptchaExpired = useCallback(() => {
    setCaptchaToken('')
  }, [])

  // Render CAPTCHA when dialog opens and turnstile is ready
  // Skip re-rendering if widget already exists - just reset the token
  useEffect(() => {
    if (open && turnstileLoaded && window.turnstile && captchaRef.current) {
      // If CAPTCHA already rendered, just reset it instead of recreating
      if (captchaRenderedRef.current && widgetIdRef.current !== null) {
        try {
          window.turnstile.reset(widgetIdRef.current)
        } catch {
          // If reset fails, the widget may have been removed - mark for re-render
          captchaRenderedRef.current = false
          widgetIdRef.current = null
        }
      }

      // Only render new widget if not already rendered
      if (!captchaRenderedRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(captchaRef.current, {
            sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
            callback: handleCaptchaSuccess,
            'expired-callback': handleCaptchaExpired,
            theme: 'light'
          })
          captchaRenderedRef.current = true
          setTurnstileError(false)
        } catch {
          // Widget rendering failed (invalid sitekey, DOM issues, etc.)
          setTurnstileError(true)
          captchaRenderedRef.current = false
          widgetIdRef.current = null
        }
      }
    }
  }, [open, turnstileLoaded, handleCaptchaSuccess, handleCaptchaExpired])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.fullName.trim()) newErrors.fullName = 'Please enter your name'
    if (!formData.email.trim()) {
      newErrors.email = 'Please enter your email'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }
    if (!formData.message.trim()) newErrors.message = 'Please tell us what you would like to know'
    if (!captchaToken) newErrors.captcha = 'Please complete the security verification'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)

    const submissionData = {
      'Form Source': 'Authentipet Contact Form',
      'Lead Type': 'General Inquiry',
      'Lead Source': 'Website Footer',
      'Submission Date': formatDate(new Date(), 'PPpp'),

      'Full Name': formData.fullName,
      'Email': formData.email,
      'Message': formData.message,

      // Cloudflare Turnstile token
      'cf-turnstile-response': captchaToken,
    }

    // Create AbortController with 10-second timeout
    const controller = new AbortController()
    abortControllerRef.current = controller
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData),
        signal: controller.signal
      })
      clearTimeout(timeoutId)
      if (!res.ok) {
        const t = await res.text()
        throw new Error(`Submission failed: ${res.status} - ${t}`)
      }
      await res.json()
      setShowSuccess(true)
      // Minimal GA4: form_submit success
      formSubmit('contact', true, Date.now() - analyticsStartRef.current)
    } catch (err) {
      clearTimeout(timeoutId)
      // Handle timeout/abort errors with user-friendly message
      if (err.name === 'AbortError') {
        setErrors({ submit: 'Request timed out. Please try again.' })
        // Minimal GA4: form_submit failure (timeout)
        formSubmit('contact', false, Date.now() - analyticsStartRef.current)
        return
      }
      setErrors({ submit: err.message })
      // Minimal GA4: form_submit failure
      formSubmit('contact', false, Date.now() - analyticsStartRef.current)
    } finally {
      setIsSubmitting(false)
      abortControllerRef.current = null
    }
  }

  const handleReset = () => {
    setFormData({ fullName: '', email: '', message: '' })
    setShowSuccess(false)
    setCaptchaToken('')
    setErrors({})
    // Reset CAPTCHA token without destroying the widget - preserves DOM for reuse
    if (window.turnstile && widgetIdRef.current !== null) {
      try {
        window.turnstile.reset(widgetIdRef.current)
      } catch {
        // If reset fails, widget may need re-rendering on next dialog open
        captchaRenderedRef.current = false
        widgetIdRef.current = null
      }
    }
  }

  const handleClose = () => {
    // Abort any in-flight request when closing the dialog
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    handleReset()
    onOpenChange(false)
  }

  if (showSuccess) {
    return (
      <div className="text-center p-6">
        <div className="w-16 h-16 bg-ap-green text-white rounded-full flex items-center justify-center mx-auto mb-4 animate-in zoom-in">
          <Check className="h-10 w-10" />
        </div>
        <h2 className="ap-hero-title text-2xl mb-2 text-white">MESSAGE SENT! 🎉</h2>
        <p className="ap-body-text text-white/80 mb-4">
          Thanks for reaching out! We'll get back to you within 24 hours.
        </p>
        <div className="flex gap-4 justify-center pt-4">
          <Button onClick={handleClose} className="bg-ap-blurple hover:bg-ap-blurple/90 text-white ap-button-text">
            Awesome! 👍
          </Button>
          <Button onClick={handleReset} variant="outline" className="text-white border-white/20 hover:bg-white/10">
            Send Another Message
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mx-auto md:max-w-2xl px-4 min-h-[100svh] flex flex-col safe-top safe-bottom">
      <div className="mb-6">
        <h2 className="ap-hero-title text-2xl mb-2 text-white">GET IN TOUCH! 📬</h2>
        <p className="ap-body-text text-white/80">
          Got questions? We've got answers! Drop us a line and we'll get back to you super quick.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex-1 overflow-y-auto overscroll-contain touch-pan-y will-change-transform pb-24 space-y-6">
        <div>
          <Label htmlFor="fullName" className="text-white">
            Your Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Your awesome name"
            className={`bg-white/5 border-white/10 text-white placeholder-white/40 ${errors.fullName ? 'border-red-500' : ''}`}
          />
          {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
        </div>

        <div>
          <Label htmlFor="email" className="text-white">
            Your Email <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className={`bg-white/5 border-white/10 text-white placeholder-white/40 ${errors.email ? 'border-red-500' : ''}`}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        <div>
          <Label htmlFor="message" className="text-white">
            What would you like us to know? <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us what's on your mind! Questions, feedback, or just say hi 👋"
            className={`bg-white/5 border-white/10 text-white placeholder-white/40 ${errors.message ? 'border-red-500' : ''}`}
          />
          {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
        </div>

        {/* Cloudflare Turnstile CAPTCHA */}
        <div className="space-y-4">
          <Label className="flex items-center gap-2 text-white">
            <ShieldCheck className="h-4 w-4 text-ap-blurple" />
            Security Verification <span className="text-red-500">*</span>
          </Label>
          <div
            ref={captchaRef}
            className={`min-h-[65px] flex items-center justify-center bg-gray-50 rounded-lg border ${turnstileError ? 'border-red-500' : 'border-gray-200'}`}
          >
            {!turnstileLoaded && (
              <p className="text-sm text-gray-500">Loading security verification...</p>
            )}
            {turnstileError && (
              <p className="text-sm text-red-500 px-4 text-center">
                Security verification failed to load. Please refresh the page and try again.
              </p>
            )}
          </div>
          {errors.captcha && (
            <p className="text-red-500 text-xs mt-1">{errors.captcha}</p>
          )}
          {turnstileError && (
            <p className="text-red-500 text-xs mt-1">
              The security widget could not be loaded. Form submission is disabled.
            </p>
          )}
          <p className="text-xs text-white/60 mt-2">
            We use Cloudflare to protect against bots and ensure you're human 🤖
          </p>
        </div>

        {/* Submission error display */}
        {errors.submit && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-red-400 text-sm">{errors.submit}</p>
          </div>
        )}

        <div className="flex gap-4 justify-end pt-4">
          <Button
            type="button"
            onClick={handleClose}
            variant="outline"
            className="text-white border-white/20 hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !turnstileLoaded || turnstileError}
            className="bg-ap-blurple hover:bg-ap-blurple/90 text-white ap-button-text"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/50 border-t-white mr-2" />
                Sending...
              </>
            ) : (
              'Send Message 🚀'
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default ContactForm
